import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { CalendarDays, Clock, MapPin, ExternalLink } from "lucide-react";

import { listQuery } from "@/lib/queries";
import { PageHero, Section, SectionTitle, EmptyState } from "@/components/site/bits";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/agenda")({
  head: () => ({
    meta: [
      { title: "Agenda | Professor Rurdiney — 40123" },
      {
        name: "description",
        content: "Confira os próximos eventos e a agenda de compromissos do Professor Rurdiney pela cidade.",
      },
      { property: "og:title", content: "Agenda | Professor Rurdiney — 40123" },
      {
        property: "og:description",
        content: "Participe dos eventos da campanha do Professor Rurdiney, candidato a vereador nº 40123.",
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(listQuery("events")),
  component: AgendaPage,
});

const STATUS_LABEL: Record<string, { label: string; className: string }> = {
  scheduled: { label: "Confirmado", className: "bg-accent text-accent-foreground" },
  cancelled: { label: "Cancelado", className: "bg-destructive text-destructive-foreground" },
  done: { label: "Realizado", className: "bg-primary text-primary-foreground" },
};

function EventCard({ e }: { e: any }) {
  const status = STATUS_LABEL[e.event_status] ?? STATUS_LABEL["scheduled"] ?? { label: "—", className: "" };
  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-14 flex-col items-center justify-center rounded-xl bg-primary/10 text-primary">
            <CalendarDays className="size-6" />
          </span>
          <div>
            <div className="font-display text-lg text-foreground">{e.title}</div>
            <div className="text-sm text-muted-foreground">{formatDate(e.event_date)}</div>
          </div>
        </div>
        <span
          className={cn(
            "shrink-0 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide",
            status.className,
          )}
        >
          {status.label}
        </span>
      </div>

      <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
        {e.event_time && (
          <p className="flex items-center gap-1.5">
            <Clock className="size-4 shrink-0" /> {e.event_time}
          </p>
        )}
        {(e.address || e.neighborhood) && (
          <p className="flex items-center gap-1.5">
            <MapPin className="size-4 shrink-0" />
            {[e.address, e.neighborhood].filter(Boolean).join(" — ")}
          </p>
        )}
      </div>

      {e.description && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{e.description}</p>
      )}

      {e.map_url && (
        <a
          href={e.map_url}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex w-fit items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-primary hover:text-accent-deep"
        >
          Ver no mapa <ExternalLink className="size-4" />
        </a>
      )}
    </div>
  );
}

function AgendaPage() {
  const { data: events } = useSuspenseQuery(listQuery("events"));
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e: any) => e.event_date >= today);
  const past = events.filter((e: any) => e.event_date < today);

  return (
    <>
      <PageHero
        eyebrow="Compromissos"
        title="Agenda"
        description="Acompanhe os próximos eventos e venha participar pessoalmente da campanha."
      />

      <Section>
        <SectionTitle eyebrow="Em breve" title="Próximos eventos" />
        {upcoming.length === 0 ? (
          <div className="mt-8">
            <EmptyState message="Nenhum evento futuro agendado no momento." />
          </div>
        ) : (
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {upcoming.map((e: any) => (
              <EventCard key={e.id} e={e} />
            ))}
          </div>
        )}
      </Section>

      <Section className="bg-secondary/50">
        <SectionTitle eyebrow="Histórico" title="Eventos anteriores" />
        {past.length === 0 ? (
          <div className="mt-8">
            <EmptyState message="Nenhum evento anterior registrado." />
          </div>
        ) : (
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {past.map((e: any) => (
              <EventCard key={e.id} e={e} />
            ))}
          </div>
        )}
      </Section>
    </>
  );
}
